<!-- Summary -->
<div class="row g-3 mb-3">
  <div class="col-12 col-md-4">
    <div class="student-card p-3 h-100">
      <p class="fs-xsmall fw-800 color-gray-500 mb-2">
        <i class="bi bi-people me-1"></i>{{'students.card_students'|translate|uppercase}}
      </p>
      <p class="fs-24 fw-800 color-gray-950 mb-0">{{summary?.studentCount ?? '—'}}</p>
      <p class="fs-small fw-400 color-gray-400 mb-0">{{'students.card_students_desc'|translate}}</p>
    </div>
  </div>

  <div class="col-12 col-md-4">
    <div class="student-card p-3 h-100">
      <p class="fs-xsmall fw-800 color-gray-500 mb-2">
        <i class="bi bi-graph-up-arrow me-1"></i>{{'students.card_score_increase'|translate|uppercase}}
      </p>
      <p class="fs-24 fw-800 mb-0"
         [class.color-brand-primary-700]="(summary?.averageScoreIncrease ?? 0) >= 0"
         [class.color-danger]="(summary?.averageScoreIncrease ?? 0) < 0">
        <ng-container *ngIf="summary?.averageScoreIncrease !== null && summary?.averageScoreIncrease !== undefined; else noIncrease">
          {{summary.averageScoreIncrease > 0 ? '+' : ''}}{{summary.averageScoreIncrease}}
        </ng-container>
        <ng-template #noIncrease>—</ng-template>
      </p>
      <p class="fs-small fw-400 color-gray-400 mb-0">
        {{'students.card_score_increase_desc'|translate: {days: summary?.windowDays ?? 30, exam: examName(examKey)} }}
      </p>
    </div>
  </div>

  <div class="col-12 col-md-4">
    <div class="student-card p-3 h-100">
      <p class="fs-xsmall fw-800 color-gray-500 mb-2">
        <i class="bi bi-camera-video me-1"></i>{{'students.card_meetings'|translate|uppercase}}
      </p>
      <p class="fs-24 fw-800 color-gray-950 mb-0">{{summary?.meetingCount ?? '—'}}</p>
      <p class="fs-small fw-400 color-gray-400 mb-0">{{'students.card_meetings_desc'|translate}}</p>
    </div>
  </div>
</div>

<div class="row g-3">

  <!-- Filters and roster share the left column; the detail of the selected student sits next to it. -->
  <div class="col-12 col-xl-4">

    <div class="student-card filters p-2 p-md-3 mb-3" [formGroup]="fg">
      <app-gray-text-input
        customClass="compact-input"
        [placeholder]="'students.search_placeholder'|translate"
        formControlName="search"></app-gray-text-input>

      <div class="exam-switch d-flex mt-2">
        <a class="exam-option flex-fill text-center fs-small fw-800 clickable py-1"
           [class.active]="examKey === examKeys.TYT" (click)="setExamKey(examKeys.TYT)">
          {{examName(examKeys.TYT)}}
        </a>
        <a class="exam-option flex-fill text-center fs-small fw-800 clickable py-1"
           [class.active]="examKey === examKeys.AYT" (click)="setExamKey(examKeys.AYT)">
          {{examName(examKeys.AYT)}}
        </a>
      </div>

      <!-- The label lives in the placeholder, the way the prototype shows "Sınıf: Tümü". -->
      <div class="row g-2 mt-1">
        <div class="col-6">
          <app-gray-select-input
            customClass="compact-select" [items]="periods" bindValue="id" bindLabel="name" [clearable]="false"
            [placeholder]="'students.period'|translate"
            formControlName="periodId"></app-gray-select-input>
        </div>

        <div class="col-6">
          <app-gray-select-input
            customClass="compact-select" [items]="grades" bindValue="id" bindLabel="name"
            [placeholder]="'students.filter_placeholder'|translate: {label: 'students.grade'|translate}"
            formControlName="gradeId"></app-gray-select-input>
        </div>

        <div class="col-6">
          <app-gray-select-input
            customClass="compact-select" [items]="levelOptions" bindValue="id" bindLabel="name"
            [placeholder]="'students.filter_placeholder'|translate: {label: 'students.level'|translate}"
            formControlName="levelId"></app-gray-select-input>
        </div>

        <div class="col-6">
          <app-gray-select-input
            customClass="compact-select" [items]="departments" bindValue="id" bindLabel="name"
            [placeholder]="'students.filter_placeholder'|translate: {label: 'students.department'|translate}"
            formControlName="departmentId"></app-gray-select-input>
        </div>
      </div>

      <!--
        TODO: COACH! The prototype has three more selects here — "Görüşme" (15'ten az / 15 – 29 /
        30 ve üzeri), "Net Artışı" (Yükselenler / Düşenler-sabit) and "Sıralama" (Varsayılan / Nete
        göre / Net artışına göre / Görüşme sayısına göre). They are left out until the roster
        endpoint can filter and sort by those derived values; see buildQueryString().
      -->
    </div>

    <ngx-skeleton-loader
      *ngIf="isLoading && !students.length" count="4" appearance="line"
      [theme]="{height: '148px', 'border-radius': '16px', margin: '0 0 8px 0'}"/>

    <div class="student-card p-4 text-center" *ngIf="!students.length && !isLoading">
      <p class="fs-normal fw-800 color-gray-950 mb-1">{{'students.empty_title'|translate}}</p>
      <p class="fs-small fw-400 color-gray-400 mb-0">{{'students.empty_desc'|translate}}</p>
    </div>

    <div class="student-card student-row p-3 mb-2 clickable level-{{levelKey(student)}}"
         *ngFor="let student of students"
         [class.selected]="student.id === selectedStudentId"
         (click)="openStudent(student.id)">

      <div class="d-flex align-items-center">
        <coach-student-avatar
          class="me-3" [name]="student.name" [levelKey]="levelKey(student)"></coach-student-avatar>

        <div class="flex-grow-1 overflow-hidden">
          <div class="d-flex align-items-center justify-content-between">
            <p class="fs-normal fw-800 color-gray-950 mb-0 text-truncate">{{student.name}}</p>
            <span class="level-badge fs-xsmall fw-800 ms-2" *ngIf="levelName(student)">
              {{levelName(student)}}
            </span>
          </div>
          <p class="fs-small fw-400 color-gray-400 mb-0 text-truncate">{{metaLine(student)}}</p>
        </div>
      </div>

      <div class="d-flex mt-2">
        <div class="score-box flex-fill me-2 p-2">
          <p class="fs-xsmall fw-500 color-gray-400 mb-0">{{examName(examKeys.TYT)|uppercase}}</p>
          <p class="fs-normal fw-800 color-gray-950 mb-0">{{student.base.score ?? '—'}}</p>
        </div>
        <div class="score-box flex-fill p-2">
          <p class="fs-xsmall fw-500 color-gray-400 mb-0">{{examName(examKeys.AYT)|uppercase}}</p>
          <p class="fs-normal fw-800 color-gray-950 mb-0">{{student.proficiency.score ?? '—'}}</p>
        </div>
      </div>

      <div class="d-flex align-items-center mt-2">
        <div class="progress-track flex-grow-1 me-2">
          <div class="progress-value" [style.width.%]="overallPercent(student)"></div>
        </div>
        <span class="fs-xsmall fw-500 color-gray-400">%{{overallPercent(student)}}</span>
      </div>

      <!-- TODO: COACH! The score trend of the active exam belongs next to the meeting count. -->
      <p class="fs-xsmall fw-400 color-gray-400 mb-0 mt-2">
        {{'students.meeting_count'|translate: {count: student.meetings.done} }}
      </p>
    </div>

    <!-- Pagination -->
    <div class="d-flex align-items-center justify-content-between flex-wrap mt-3" *ngIf="lastPage > 1">
      <p class="fs-small fw-400 color-gray-400 mb-0">
        {{'students.total_count'|translate: {count: total} }}
      </p>

      <nav class="pager d-flex align-items-center">
        <a class="page-item clickable" [class.disabled]="page === 1" (click)="goToPage(page - 1)">
          <i class="bi bi-chevron-left"></i>
        </a>
        <a class="page-item clickable fs-small fw-800" *ngFor="let item of pageNumbers()"
           [class.active]="item === page" (click)="goToPage(item)">{{item}}</a>
        <a class="page-item clickable" [class.disabled]="page === lastPage" (click)="goToPage(page + 1)">
          <i class="bi bi-chevron-right"></i>
        </a>
      </nav>
    </div>

  </div>

  <!-- Detail -->
  <div class="col-12 col-xl-8 detail-panel" #detailPanel>
    <coach-student-detail *ngIf="selectedStudentId" [studentId]="selectedStudentId"></coach-student-detail>

    <div class="student-card p-5 text-center" *ngIf="!selectedStudentId">
      <p class="fs-normal fw-800 color-gray-950 mb-1">{{'students.select_title'|translate}}</p>
      <p class="fs-small fw-400 color-gray-400 mb-0">{{'students.select_desc'|translate}}</p>
    </div>
  </div>

</div>
